import { useEffect, useState, useCallback } from "react";
import { api } from "../../api";
import { toast } from "sonner";
import { Plus, Trash2, X, Mail, Check, ShieldCheck, Info } from "lucide-react";
import { useSortedList } from "../../components/useSortedList";
import { useConfirm } from "../../components/Confirm";
import { useDataVersion } from "../../hooks/useAppShell";

export default function TeamAccess({ user }) {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "reader" });

  // Super admins have full control of Team Access.
  // Regular admins can invite reader/editor members and send invites, but cannot
  // change existing roles or delete anyone — those are locked to super admins.
  const isSuperAdmin = !!user.is_super_admin;

  const SORTABLE = [{ key: "email", label: "Email" }, { key: "name", label: "Name" }, { key: "role", label: "Role" }, { key: "added_by", label: "Added By" }];
  const { sorted: sortedTeam, SortBar, sortBarProps, Pagination, paginationProps } = useSortedList(team, SORTABLE);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try { setTeam(await api.team.list()); } catch (e) { toast.error(String(e.message || e)); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  const globalVersion = useDataVersion();
  useEffect(() => { if (globalVersion > 0) load(); }, [globalVersion, load]);

  const add = async (e) => {
    e.preventDefault();
    try { await api.team.add(form); toast.success("Added"); setShowAdd(false); setForm({ email: "", name: "", role: "reader" }); load(); }
    catch (e) { toast.error(String(e.message || e)); }
  };
  const changeRole = async (m, role) => {
    try { await api.team.update(m.id, { ...m, role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(String(e.message || e)); }
  };
  const remove = (m) => {
    confirm(
      `Revoke access for ${m.email}? They won't be able to sign in anymore, and any active session will be terminated immediately.`,
      async () => {
        try { await api.team.remove(m.id); toast.success("Removed"); load(); }
        catch (e) { toast.error(String(e.message || e)); }
      },
      { title: "Revoke access?", confirmLabel: "Yes, revoke", cancelLabel: "Keep access" }
    );
  };
  const invite = async (m) => {
    try { await api.invite(m.id); toast.success(`Welcome email sent to ${m.email}`); load(); }
    catch (e) { toast.error(String(e.message || e)); }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
        <div>
          <div className="muted" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" }}>System</div>
          <h1 style={{ fontSize: 30, margin: "4px 0 0 0" }}>Team Access</h1>
          <p className="muted" style={{ marginTop: 8, fontSize: 12, maxWidth: 640 }}>
            Only Gmail addresses added here can sign in. {isSuperAdmin
              ? "You're a super admin — you can grant admin access, change roles, and revoke access."
              : "As a regular admin you can invite readers and editors. Only super admins can grant admin access, change roles, or revoke access."
            }
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)} data-testid="add-team-btn"><Plus size={14} /> Grant access</button>
      </div>

      {!isSuperAdmin && (
        <div
          className="panel"
          data-testid="team-access-permission-banner"
          style={{
            padding: "12px 16px",
            marginBottom: 16,
            background: "rgba(124,108,255,0.06)",
            border: "1px solid rgba(124,108,255,0.28)",
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontSize: 12.5,
          }}
        >
          <Info size={14} style={{ color: "var(--violet)", flexShrink: 0 }} />
          <span>
            You're viewing Team Access as a regular admin. You can invite readers &amp; editors but cannot change roles or revoke access — those are locked to super admins.
          </span>
        </div>
      )}

      {showAdd && (
        <form onSubmit={add} className="panel" style={{ padding: 22, marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
            <h3 style={{ fontSize: 16 }}>Grant access</h3>
            <button type="button" className="btn" onClick={() => setShowAdd(false)}><X size={14} /></button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
            <label style={{ fontSize: 12 }}>
              <span className="muted" style={{ display: "block", marginBottom: 5 }}>Gmail address *</span>
              <input className="input" type="email" required value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} data-testid="field-team-email" />
            </label>
            <label style={{ fontSize: 12 }}>
              <span className="muted" style={{ display: "block", marginBottom: 5 }}>Name (optional)</span>
              <input className="input" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} data-testid="field-team-name" />
            </label>
            <label style={{ fontSize: 12 }}>
              <span className="muted" style={{ display: "block", marginBottom: 5 }}>Role</span>
              <select className="input" value={form.role} onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))} data-testid="field-team-role">
                <option value="reader">Reader</option>
                <option value="editor">Editor</option>
                {isSuperAdmin && <option value="admin">Admin</option>}
              </select>
              {!isSuperAdmin && (
                <span className="muted" style={{ fontSize: 10.5, marginTop: 4, display: "block" }}>
                  Only super admins can grant admin access.
                </span>
              )}
            </label>
          </div>
          <div style={{ marginTop: 18, display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button type="button" className="btn" onClick={() => setShowAdd(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" data-testid="submit-team">Grant access</button>
          </div>
        </form>
      )}

      <div className="panel" style={{ overflow: "hidden" }}>
        {loading ? <div style={{ padding: 20 }} className="muted">Loading…</div> :
         team.length === 0 ? <div style={{ padding: 40, textAlign: "center" }} className="muted">No team members yet.</div> :
        (
          <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid var(--border)", flexWrap: "wrap", gap: 12 }}>
            <SortBar {...sortBarProps} />
            <span className="muted" style={{ fontSize: 11 }} data-testid="entry-count">{paginationProps.total} member{paginationProps.total === 1 ? "" : "s"}</span>
          </div>
          <table>
            <thead><tr><th>Email</th><th>Name</th><th>Role</th><th>Added by</th><th>Invite</th><th style={{ textAlign: "right" }}></th></tr></thead>
            <tbody>
              {sortedTeam.map(m => {
                const roleEditable = isSuperAdmin && !m.is_super_admin;
                const canRemove = isSuperAdmin && !m.is_super_admin;
                return (
                  <tr key={m.id} data-testid={`row-team-${m.email}`}>
                    <td style={{ fontWeight: 500 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span>{m.email}</span>
                        {m.is_super_admin && (
                          <span
                            className="badge"
                            data-testid={`super-admin-badge-${m.email}`}
                            title="Protected owner account. Cannot be removed or demoted from the UI."
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              padding: "2px 8px", fontSize: 10, letterSpacing: "0.08em",
                              background: "rgba(124,108,255,0.14)", color: "#c4b8ff",
                              border: "1px solid rgba(124,108,255,0.35)",
                            }}
                          >
                            <ShieldCheck size={10} /> PROTECTED
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{m.name || <span className="muted">—</span>}</td>
                    <td>
                      {m.is_super_admin ? (
                        <span className="badge badge-brand" style={{ padding: "4px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em" }} data-testid={`role-locked-${m.email}`}>
                          Admin (locked)
                        </span>
                      ) : roleEditable ? (
                        <select className="input" style={{ maxWidth: 140 }} value={m.role} onChange={(e) => changeRole(m, e.target.value)} data-testid={`role-${m.email}`}>
                          <option value="reader">Reader</option>
                          <option value="editor">Editor</option>
                          <option value="admin">Admin</option>
                        </select>
                      ) : (
                        <span
                          className="badge"
                          data-testid={`role-readonly-${m.email}`}
                          title="Only super admins can change roles"
                          style={{ padding: "4px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em" }}
                        >
                          {m.role}
                        </span>
                      )}
                    </td>
                    <td className="muted" style={{ fontSize: 12 }}>{m.added_by}</td>
                    <td>
                      {m.invited_at ? (
                        <span className="badge badge-mint" style={{ display: "inline-flex", alignItems: "center", gap: 5 }} title={new Date(m.invited_at).toLocaleString()}>
                          <Check size={11} /> Sent
                        </span>
                      ) : (
                        <button className="btn" onClick={() => invite(m)} data-testid={`invite-${m.email}`} style={{ padding: "5px 10px", fontSize: 12 }}>
                          <Mail size={12} /> Send invite
                        </button>
                      )}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {m.is_super_admin ? (
                        <span className="muted" title="Protected account — cannot be removed" style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }} data-testid={`protected-lock-${m.email}`}>
                          <ShieldCheck size={12} /> Locked
                        </span>
                      ) : canRemove ? (
                        <button className="btn btn-danger" onClick={() => remove(m)} data-testid={`del-team-${m.email}`} style={{ padding: "5px 9px", fontSize: 12 }}>
                          <Trash2 size={12} />
                        </button>
                      ) : (
                        <span className="muted" title="Only super admins can revoke access" style={{ fontSize: 11 }} data-testid={`del-locked-${m.email}`}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination {...paginationProps} />
          </>
        )}
      </div>
    </div>
  );
}
